import { useMemo, type CSSProperties, type ReactNode } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { compact } from "lodash";

import { type SavedSearchTarget } from "@features/search-input/types";
import { type ExtendedRefs, type ReferenceType } from "@floating-ui/react";
import {
  SavedSearchMethod,
  type FetchSavedSearches_me_savedSearches,
} from "@north-beam/nb-client-side";

import { SavedSearchesSubmenu } from "./saved-searches-submenu";

interface Props {
  refs: ExtendedRefs<ReferenceType>;
  target: SavedSearchTarget;
  floatingStyles: CSSProperties;
  isOpen: boolean;
  savedSearches?: FetchSavedSearches_me_savedSearches[];
  recentSavedSearchIds: string[];
  onChangeSearchMethod(value: SavedSearchMethod): void;
  onSelectSavedSearch(value: FetchSavedSearches_me_savedSearches): void;
  onDeleteSavedSearch(value: FetchSavedSearches_me_savedSearches): void;
}

export const SearchMenu = ({
  refs,
  target,
  floatingStyles,
  isOpen,
  savedSearches,
  recentSavedSearchIds,
  onChangeSearchMethod,
  onSelectSavedSearch,
  onDeleteSavedSearch,
}: Props) => {
  const recentSavedSearches = useMemo(() => {
    return compact(
      recentSavedSearchIds.map((e) => savedSearches?.find((v) => v.id === e)),
    );
  }, [savedSearches, recentSavedSearchIds]);

  return (
    <div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            ref={refs.setFloating}
            style={floatingStyles}
            className={clsx(
              "absolute z-10 pt-3 pb-2 w-full mt-2",
              "bg-white rounded-lg elevation-1 text-sm",
            )}
          >
            {recentSavedSearches.length > 0 && (
              <>
                <div className="text-gray+1 text-xs font-light mx-4 mb-2">
                  Recent
                </div>
                {recentSavedSearches.map((e) => (
                  <Option
                    key={e.id}
                    onClick={() => {
                      onSelectSavedSearch(e);
                    }}
                  >
                    {e.name}
                  </Option>
                ))}
                <div className="border-b border-gray-1"></div>
              </>
            )}
            {savedSearches && (
              <SavedSearchesSubmenu
                target={target}
                savedSearches={savedSearches}
                onSelectSavedSearch={(value) => {
                  onSelectSavedSearch(value);
                }}
                onDeleteSavedSearch={onDeleteSavedSearch}
              />
            )}
            <Option
              onClick={() => onChangeSearchMethod(SavedSearchMethod.regex)}
            >
              <i className="fa-regular fa-sharp fa-function text-[0.8rem] text-primary mr-2 w-4"></i>
              regEx search
            </Option>
            <Option
              onClick={() => onChangeSearchMethod(SavedSearchMethod.comma)}
            >
              <i className="fa-regular fa-sharp fa-list text-[0.8rem] text-primary mr-2 w-4"></i>
              Comma separated search
            </Option>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Option = ({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?(): void;
}) => (
  <div
    className={clsx(
      "w-full text-left py-1.5 px-4 hover:bg-cosmic-4 truncate cursor-pointer",
      className,
    )}
    onClick={onClick}
  >
    {children}
  </div>
);
